import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import typeProfiles from "../data/typeProfiles";
import MatchLab from "../component/MatchLab";
import "./ResultApp.css";

const titles = {
  istj: ["Logistician", "\ud604\uc2e4\uc801\uc778 \uad00\ub9ac\uc790"], isfj: ["Defender", "\ub530\ub73b\ud55c \uc218\ud638\uc790"], infj: ["Advocate", "\ud1b5\ucc30\ub825 \uc788\ub294 \uc870\uc5b8\uc790"], intj: ["Architect", "\uc804\ub7b5\uc801\uc778 \uc124\uacc4\uc790"],
  istp: ["Virtuoso", "\uc720\uc5f0\ud55c \ud574\uacb0\uc0ac"], isfp: ["Adventurer", "\uac10\uac01\uc801\uc778 \ud0d0\ud5d8\uac00"], infp: ["Mediator", "\uc774\uc0c1\uc744 \ud488\uc740 \uc911\uc7ac\uc790"], intp: ["Logician", "\ud638\uae30\uc2ec \ub9ce\uc740 \ubd84\uc11d\uac00"],
  estp: ["Entrepreneur", "\ub300\ub2f4\ud55c \uc2e4\ud589\uac00"], esfp: ["Entertainer", "\uc790\uc720\ub85c\uc6b4 \ubd84\uc704\uae30 \uba54\uc774\ucee4"], enfp: ["Campaigner", "\uc5f4\uc815\uc801\uc778 \ud0d0\ud5d8\uac00"], entp: ["Debater", "\uc544\uc774\ub514\uc5b4\ud615 \ub3c4\uc804\uc790"],
  estj: ["Executive", "\uccb4\uacc4\uc801\uc778 \uc9c0\ud718\uad00"], esfj: ["Consul", "\ub2e4\uc815\ud55c \ucee4\ub125\ud130"], enfj: ["Protagonist", "\uc0ac\ub78c\uc744 \uc774\ub044\ub294 \uc548\ub0b4\uc790"], entj: ["Commander", "\ubaa9\ud45c \uc9c0\ud5a5\uc801 \uc0ac\ub839\uad00"],
};

const axes = [
  { pair: ["e", "i"], ko: "\uc5d0\ub108\uc9c0", en: "Energy" },
  { pair: ["s", "n"], ko: "\uc815\ubcf4 \uc778\uc2dd", en: "Information" },
  { pair: ["t", "f"], ko: "\uc758\uc0ac \uacb0\uc815", en: "Decision" },
  { pair: ["j", "p"], ko: "\uc0dd\ud65c \ubc29\uc2dd", en: "Lifestyle" },
];

const sectionNames = {
  overview: { ko: "\ud575\uc2ec \uc131\ud5a5", en: "Core pattern" },
  strengths: { ko: "\ub098\uc758 \uac15\uc810", en: "Natural strengths" },
  watch: { ko: "\uc8fc\uc758\ud560 \uc810", en: "Watch-outs" },
  relations: { ko: "\uc778\uac04\uad00\uacc4\uc640 \uc0ac\ub791", en: "Relationships" },
  work: { ko: "\uc77c\ud558\ub294 \ubc29\uc2dd", en: "Work style" },
  growth: { ko: "\uc131\uc7a5 \ud301", en: "Growth experiments" },
  mission: { ko: "COOLDOC \uc9c0\uad6c \ud0d0\uc0ac \ud574\uc11d", en: "COOLDOC mission reading" },
};

const ResultSection = ({ title, children, accent = false }) => (
  <section className={`report-section${accent ? " accent" : ""}`}>
    <h2>{title}</h2>{children}
  </section>
);

const Result = () => {
  const { id: rawId } = useParams();
  const history = useHistory();
  const id = (rawId || "").toLowerCase();
  const [lang, setLang] = useState("en");
  const isBasic = localStorage.getItem("cooldoc_result_depth") === "basic";
  const title = titles[id];
  const profile = typeProfiles[id];
  const scores = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("cooldoc_scores")) || {}; }
    catch (_) { return {}; }
  }, []);

  useEffect(() => { setLang(localStorage.getItem("cooldoc_lang") === "ko" ? "ko" : "en"); }, []);
  useEffect(() => { if (!title || !profile) history.replace("/"); }, [title, profile, history]);

  const preferenceRows = useMemo(() => axes.map((axis) => {
    const [left, right] = axis.pair;
    const total = (scores[left] || 0) + (scores[right] || 0);
    const leftPercent = total ? Math.round(((scores[left] || 0) / total) * 100) : 50;
    return { ...axis, left, right, leftPercent, rightPercent: 100 - leftPercent };
  }), [scores]);

  const unlockFullAnalysis = () => {
    if (Capacitor.isNativePlatform()) {
      localStorage.setItem("cooldoc_full_beta_access", "true");
      history.push("/test?continue=1");
      return;
    }
    const button = document.querySelector(".install-dock button");
    if (button) button.click();
    else window.alert(lang === "ko" ? "먼저 COOLDOC 앱을 설치한 뒤 앱에서 전체 분석을 선택해주세요." : "Install COOLDOC first, then choose Full Analysis in the app.");
  };

  const shareResult = async () => {
    const data = { title: `COOLDOC - ${id.toUpperCase()}`, text: lang === "ko" ? `\uc9c0\uad6c\uc778 \uc815\uccb4\uc131 \ubd84\uc11d \uacb0\uacfc\ub294 ${id.toUpperCase()}!` : `Earthling identity result: ${id.toUpperCase()}!`, url: window.location.href };
    if (navigator.share) await navigator.share(data).catch(() => {});
    else { await navigator.clipboard.writeText(window.location.href); window.alert(lang === "ko" ? "\uacb0\uacfc \ub9c1\ud06c\ub97c \ubcf5\uc0ac\ud588\uc2b5\ub2c8\ub2e4." : "Result link copied."); }
  };

  if (!title || !profile) return null;
  const pick = (value) => value[lang];
  return (
    <main className={`container result result-app ${isBasic ? "basic-result" : ""}`}>
      <img className="logo" src="/image/logo-3d.png" alt="COOLDOC" />
      <section className="result-hero">
        <div className="result-kicker">{lang === "ko" ? (isBasic ? "12문항 코어 스캔 완료" : "32문항 심화 스캔 완료") : (isBasic ? "12-question core scan complete" : "32-question deep scan complete")}</div>
        <h1 className="result-header"><span className="neon">{id}</span><span className="sub-text">{title[lang === "ko" ? 1 : 0]}</span></h1>
        <p className="hero-overview">{pick(profile.overview)}</p>
      </section>

      {isBasic && <section className="basic-snapshot">
        <div className="section-eyebrow">{lang === "ko" ? "12문항에서 확인된 핵심" : "Your 12-question core signal"}</div>
        <div className="basic-insight-grid">
          <article><h2>{lang === "ko" ? "먼저 보이는 강점" : "Strengths already visible"}</h2><ul>{profile.strengths.slice(0, 2).map((item, index) => <li key={index}>{pick(item)}</li>)}</ul></article>
          <article><h2>{lang === "ko" ? "한 가지 체크 포인트" : "One watch point"}</h2><p>{pick(profile.watch[0])}</p></article>
        </div>
        <p className="basic-sample-note">{lang === "ko" ? "네 성향축을 각 3문항씩 같은 비중으로 확인한 코어 결과입니다. 비슷한 성향은 상황에 따라 달라질 수 있습니다." : "Each of the four axes was checked with three equally weighted questions. Close preferences may shift by context."}</p>
      </section>}

      <section className="preference-report">
        <div className="section-eyebrow">{lang === "ko" ? "32\ubb38\ud56d \uc120\ud638 \ubd84\uc11d" : "32-question preference scan"}</div>
        <h2>{lang === "ko" ? "\ub098\uc758 \uc120\ud638 \uc9c0\ud45c" : "Your preference indicators"}</h2>
        {preferenceRows.map((row) => (
          <div className="preference-row" key={row.left}>
            <div className="preference-heading"><span>{row[lang]}</span><strong>{row.left.toUpperCase()} {row.leftPercent}% / {row.rightPercent}% {row.right.toUpperCase()}</strong></div>
            <div className="preference-track"><span style={{ width: `${row.leftPercent}%` }} /></div>
          </div>
        ))}
        <p className="result-note">{lang === "ko" ? "\uc218\uce58\ub294 \uc774 \ud0d0\uc0ac\uc5d0\uc11c \uc120\ud0dd\ud55c \ub2f5\ubcc0\uc758 \ube44\uc728\uc774\uba70, \uc758\ub8cc\uc801\uc774\ub098 \uc2ec\ub9ac\ud559\uc801 \uc9c4\ub2e8\uc774 \uc544\ub2d9\ub2c8\ub2e4." : "These percentages reflect answers in this mission and are not a medical or psychological diagnosis."}</p>
      </section>

      <div className="report-grid two-column">
        <ResultSection title={sectionNames.strengths[lang]}><ul>{profile.strengths.map((item, index) => <li key={index}>{pick(item)}</li>)}</ul></ResultSection>
        <ResultSection title={sectionNames.watch[lang]}><ul>{profile.watch.map((item, index) => <li key={index}>{pick(item)}</li>)}</ul></ResultSection>
      </div>
      <ResultSection title={sectionNames.relations[lang]}><p>{pick(profile.relations)}</p></ResultSection>
      <ResultSection title={sectionNames.work[lang]}><p>{pick(profile.work)}</p></ResultSection>
      <ResultSection title={sectionNames.growth[lang]}><ol>{profile.growth.map((item, index) => <li key={index}>{pick(item)}</li>)}</ol></ResultSection>
      <ResultSection title={sectionNames.mission[lang]} accent><p>{pick(profile.mission)}</p></ResultSection>

      <MatchLab userType={id} lang={lang} />

      <p className="result-disclaimer">{lang === "ko" ? "MBTI \uc720\ud615\uc740 \uc131\uaca9\uc758 \uc6b0\uc5f4\uc774 \uc544\ub2c8\ub77c \ud3b8\ud558\uac8c \uc0ac\uc6a9\ud558\ub294 \uc120\ud638\ub97c \uc774\ud574\ud558\ub294 \ub80c\uc988\uc785\ub2c8\ub2e4. \uc0c1\ud669, \uacbd\ud5d8, \uc131\uc7a5\uc5d0 \ub530\ub77c \ud589\ub3d9\uc740 \ub2ec\ub77c\uc9c8 \uc218 \uc788\uc2b5\ub2c8\ub2e4." : "MBTI describes preferences, not ability or worth. Behavior can change with context, experience, and development."}</p>
      {isBasic && <section className="basic-upgrade">
        <div className="upgrade-badge">{lang === "ko" ? "FULL SCAN · 20문항 추가" : "FULL SCAN · 20 MORE"}</div>
        <strong>{lang === "ko" ? "결과가 맞는 이유까지 확인하세요" : "See why this result fits you"}</strong>
        <p>{lang === "ko" ? "관계 반응, 의사결정, 스트레스 패턴, 성장 포인트와 타입 매칭을 더 세밀하게 분석합니다." : "Unlock deeper patterns for relationships, decisions, stress, growth, and type matching."}</p>
        <ul><li>{lang === "ko" ? "4개 성향축 전체 강도" : "Strength across all four axes"}</li><li>{lang === "ko" ? "관계·업무·성장 상세 해설" : "Relationship, work, and growth detail"}</li><li>{lang === "ko" ? "두 사람 타입 매칭 리포트" : "Two-person type matching report"}</li></ul>
        <button className="upgrade-primary" onClick={unlockFullAnalysis}>{lang === "ko" ? "전체 분석 잠금 해제 · 5,900원" : "Unlock full analysis · ₩5,900"}</button>
        <small>{lang === "ko" ? "정식 출시 전 베타 테스트에서는 무료로 체험할 수 있습니다." : "Free during beta testing before the official release."}</small>
      </section>}
      <div className="result-actions">
        <button className="cta share-result" onClick={shareResult}>{lang === "ko" ? "\uacb0\uacfc \uacf5\uc720" : "Share result"}</button>
        <button className="secondary-action" onClick={() => history.push("/")}>{lang === "ko" ? "\ub2e4\uc2dc \ud0d0\uc0ac" : "Restart mission"}</button>
      </div>
    </main>
  );
};

export default Result;




